import { RefreshCw } from "lucide-react";
import { useMemo } from "react";
import { useStore } from "../app/store.ts";
import { DraggablePanel } from "./DraggablePanel.tsx";
import { useDeferredUndo } from "./hooks/useDeferredUndo.ts";
import { StarFilledIcon, StarIcon } from "./Icons.tsx";
import { Slider } from "./Slider.tsx";
import { SidebarButton } from "./shared.tsx";
import { GLASS_PANEL } from "./styles.ts";
import {
  COLOR_GOLD,
  COLOR_TEXT_DIM,
  COLOR_TEXT_DISABLED,
  COLOR_TEXT_SECONDARY,
  FONT_SIZE_BODY,
  FONT_SIZE_TINY,
  PANEL_BOTTOM_OFFSET,
  Z_FLOATING,
} from "./tokens.ts";

interface FavoriteEntry {
  setRef: string;
  keyName: string;
  objectName: string;
  weight: number;
  defaultValue: number;
  min: number;
  max: number;
  isDriven: boolean;
}

export function FavoritesPanel({ onClose }: { onClose?: () => void }) {
  const blendFiles = useStore((s) => s.blendFiles);
  const favorites = useStore((s) => s.favoriteShapeKeys);
  const shapeKeyWeights = useStore((s) => s.shapeKeyWeights);
  const setWeight = useStore((s) => s.setShapeKeyWeight);
  const toggleFav = useStore((s) => s.toggleFavoriteShapeKey);
  const driverGraph = useStore((s) => s.driverGraph);
  const undo = useDeferredUndo();

  const entries = useMemo(() => {
    if (blendFiles.size === 0 || favorites.size === 0) return [];

    const setToObj = new Map<string, string>();
    const allSKSets = new Map<
      string,
      {
        keys: {
          name: string;
          defaultValue: number;
          min: number;
          max: number;
        }[];
      }
    >();
    for (const entry of blendFiles.values()) {
      for (const obj of entry.file.objects) {
        if (obj.shapeKeySetRef) {
          setToObj.set(obj.shapeKeySetRef, obj.name);
        }
      }
      for (const [key, sks] of entry.file.shapeKeySets) {
        allSKSets.set(key, sks);
      }
    }

    const result: FavoriteEntry[] = [];
    for (const fav of favorites) {
      const sep = fav.indexOf("::");
      if (sep < 0) continue;
      const setRef = fav.slice(0, sep);
      const keyName = fav.slice(sep + 2);
      const objectName = setToObj.get(setRef) ?? "Unknown";
      const weight = shapeKeyWeights.get(setRef)?.get(keyName) ?? 0;

      const skSet = allSKSets.get(setRef);
      const keyDef = skSet?.keys.find((k) => k.name === keyName);
      const isDriven =
        driverGraph?.getDrivenKeysForSet(setRef)?.has(keyName) ?? false;

      result.push({
        setRef,
        keyName,
        objectName,
        weight,
        defaultValue: keyDef?.defaultValue ?? 0,
        min: keyDef?.min ?? 0,
        max: keyDef?.max ?? 1,
        isDriven,
      });
    }
    return result;
  }, [blendFiles, favorites, shapeKeyWeights, driverGraph]);

  return (
    <DraggablePanel
      id="favorites-panel"
      defaultPosition={{
        bottom: PANEL_BOTTOM_OFFSET,
        left: window.innerWidth / 2 - 170,
      }}
      zIndex={Z_FLOATING}
      onClose={onClose}
      collapsible
      header={
        <>
          <span
            style={{
              display: "flex",
              alignItems: "center",
              color: COLOR_TEXT_DIM,
            }}
          >
            <StarIcon />
          </span>
          Favorites
        </>
      }
      style={{ width: 340 }}
    >
      <div
        style={{
          background: GLASS_PANEL.background,
          backdropFilter: GLASS_PANEL.backdropFilter as string,
          WebkitBackdropFilter: GLASS_PANEL.backdropFilter as string,
          borderTop: "none",
          borderRight: GLASS_PANEL.border,
          borderBottom: GLASS_PANEL.border,
          borderLeft: GLASS_PANEL.border,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomLeftRadius: 16,
          borderBottomRightRadius: 16,
          padding: "4px 20px 16px",
        }}
      >
        {entries.length === 0 && (
          <div
            style={{
              fontSize: FONT_SIZE_BODY,
              color: COLOR_TEXT_DISABLED,
              padding: "4px 0",
            }}
          >
            No favorites yet. Star shape keys in the Scene panel.
          </div>
        )}

        {entries.length > 0 && (
          <div style={{ padding: "4px 0 8px" }}>
            <SidebarButton
              icon={<RefreshCw size={14} />}
              label="Reset All"
              active={false}
              onClick={() => {
                const s = useStore.getState();
                s.pushUndo();
                const weights = new Map(s.shapeKeyWeights);
                for (const entry of entries) {
                  const kw =
                    weights.get(entry.setRef) ?? new Map<string, number>();
                  kw.set(entry.keyName, entry.defaultValue);
                  weights.set(entry.setRef, new Map(kw));
                }
                useStore.setState({ shapeKeyWeights: weights });
              }}
            />
          </div>
        )}

        {entries.map((entry) => (
          <div
            key={`${entry.setRef}::${entry.keyName}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "5px 0",
              fontSize: FONT_SIZE_BODY,
            }}
          >
            <span
              onClick={() => toggleFav(entry.setRef, entry.keyName)}
              style={{
                cursor: "pointer",
                color: COLOR_GOLD,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
              }}
            >
              <StarFilledIcon />
            </span>
            <div
              style={{
                width: 100,
                flexShrink: 0,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  color: COLOR_TEXT_SECONDARY,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: FONT_SIZE_BODY,
                }}
              >
                {entry.keyName}
              </div>
              <div
                style={{
                  color: COLOR_TEXT_DISABLED,
                  fontSize: FONT_SIZE_TINY,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {entry.objectName}
              </div>
            </div>
            <Slider
              min={entry.min * 100}
              max={entry.max * 100}
              value={entry.weight * 100}
              defaultValue={entry.defaultValue * 100}
              disabled={entry.isDriven}
              onStart={undo.pushOnce}
              onEnd={undo.reset}
              onChange={(v) => setWeight(entry.setRef, entry.keyName, v / 100)}
              dimChromeOnDrag
            />
            <span
              style={{
                width: 38,
                textAlign: "right",
                color: COLOR_TEXT_DIM,
                fontSize: FONT_SIZE_BODY,
                fontVariantNumeric: "tabular-nums",
                flexShrink: 0,
              }}
            >
              {entry.weight.toFixed(2)}
            </span>
          </div>
        ))}
      </div>
    </DraggablePanel>
  );
}
